import type { InboxType } from '@/services/inbox/inbox.service';

/**
 * Fonte única de verdade sobre plataformas de inbox no front.
 *
 * Antes desta feature o mapeamento (rótulo, ícone, "é oficial?", "tem QR?")
 * estava duplicado inline em mais de oito componentes, e cada nova plataforma
 * exigia caçar todas as ocorrências. As funções abaixo descrevem CAPACIDADES,
 * não plataformas — é o que a UI realmente precisa perguntar.
 */

/** Uma peça do selo visual: ícone de marca, imagem de provedor ou o "+". */
export type PecaPlataforma =
  | { icon: string; brand: string; plus?: never; img?: never }
  | { img: string; icon?: never; brand?: never; plus?: never }
  | { plus: true; icon?: never; brand?: never; img?: never };

export interface ConfigPlataforma {
  label: string;
  parts: PecaPlataforma[];
}

/**
 * Rótulo + composição visual por plataforma. Consumido por `PlataformaBadge`
 * (select do ModalInbox, coluna "Plataforma" da tabela admin) para que a
 * apresentação seja idêntica em todo lugar.
 */
export const PLATAFORMAS: Record<string, ConfigPlataforma> = {
  INSTAGRAM_LOGIN: {
    label: 'Instagram',
    parts: [{ icon: 'ri-instagram-line', brand: 'instagram' }],
  },
  INSTAGRAM_META: {
    label: 'Instagram (Meta)',
    parts: [
      { icon: 'ri-instagram-line', brand: 'instagram' },
      { plus: true },
      { icon: 'ri-meta-fill', brand: 'meta' },
    ],
  },
  MESSENGER_META: {
    label: 'Messenger (Meta)',
    parts: [
      { icon: 'ri-messenger-line', brand: 'messenger' },
      { plus: true },
      { icon: 'ri-meta-fill', brand: 'meta' },
    ],
  },
  WHATSAPP_META: {
    label: 'WhatsApp (Meta)',
    parts: [
      { icon: 'ri-whatsapp-line', brand: 'whatsapp' },
      { plus: true },
      { icon: 'ri-meta-fill', brand: 'meta' },
    ],
  },
  // Coexistência: as DUAS conexões no mesmo número — o selo mostra as duas.
  WHATSAPP_HYBRID_COEXISTENCE: {
    label: 'WhatsApp Coexistência Híbrida (Meta + Evolution)',
    parts: [
      { icon: 'ri-whatsapp-line', brand: 'whatsapp' },
      { plus: true },
      { icon: 'ri-meta-fill', brand: 'meta' },
      { plus: true },
      { img: '/img/wpp-providers/evolution.png' },
    ],
  },
  WHATSAPP_EVOLUTION: {
    label: 'WhatsApp - Evolution',
    parts: [
      { icon: 'ri-whatsapp-line', brand: 'whatsapp' },
      { plus: true },
      { img: '/img/wpp-providers/evolution.png' },
    ],
  },
  WHATSAPP_ZAPI: {
    label: 'WhatsApp - Z-API',
    parts: [
      { icon: 'ri-whatsapp-line', brand: 'whatsapp' },
      { plus: true },
      { img: '/img/wpp-providers/z-api.png' },
    ],
  },
};

export const rotuloPlataforma = (plataforma?: string | null): string =>
  (plataforma && PLATAFORMAS[plataforma]?.label) || plataforma || 'Sem plataforma';

/**
 * Rótulo compacto `CANAL/TRANSPORTE`, para linhas estreitas onde o valor cru do
 * enum vazava para o usuário (ex.: a lista lateral do InboxModal, que mostrava
 * `WHATSAPP_HYBRID_COEXISTENCE`).
 *
 * Separado de {@link PLATAFORMAS} de propósito: aquele rótulo é o nome por
 * extenso usado em selects e tabelas, este é a versão que cabe em ~140px.
 */
export const ROTULOS_CURTOS_PLATAFORMA: Record<string, string> = {
  WHATSAPP_EVOLUTION: 'WHATSAPP/EVOLUTION',
  WHATSAPP_ZAPI: 'WHATSAPP/Z-API',
  WHATSAPP_META: 'WHATSAPP/META CLOUD API',
  WHATSAPP_COEXISTENCE: 'WHATSAPP/COEXISTÊNCIA',
  WHATSAPP_HYBRID_COEXISTENCE: 'WHATSAPP/COEX. HÍBRIDA',
  INSTAGRAM_META: 'INSTAGRAM/LOGIN META',
  INSTAGRAM_LOGIN: 'INSTAGRAM/LOGIN DIRETO',
  MESSENGER_META: 'MESSENGER/LOGIN META',
};

/**
 * Sem plataforma → `''`, para quem chama decidir o texto de vazio com um `||`.
 *
 * Plataforma sem rótulo curto cai no rótulo longo e, em último caso, no valor
 * cru: uma plataforma nova aparecendo feia é um bug visível, e é melhor que
 * fingir "sem plataforma" para um inbox que tem uma.
 */
export const rotuloCurtoPlataforma = (plataforma?: string | null): string =>
  plataforma
    ? ROTULOS_CURTOS_PLATAFORMA[plataforma] ?? PLATAFORMAS[plataforma]?.label ?? plataforma
    : '';

/**
 * Inbox que fala WhatsApp em qualquer transporte (contato é telefone,
 * agendamento/campanha fazem sentido).
 */
export const ehWhatsapp = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_EVOLUTION' ||
  plataforma === 'WHATSAPP_ZAPI' ||
  plataforma === 'WHATSAPP_META' ||
  plataforma === 'WHATSAPP_HYBRID_COEXISTENCE';

/**
 * Inbox WhatsApp **exclusivamente** oficial. Este é o gate das regras rígidas
 * da Cloud API: janela de atendimento de 24h, só template em campanha/
 * agendamento, sem editar/apagar mensagem, sem grupos.
 *
 * Coexistência NÃO entra aqui de propósito: lá existe a metade Evolution para
 * fazer tudo isso.
 */
export const ehWhatsappSomenteOficial = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_META';

/**
 * Superfícies sociais da Meta (Instagram e Messenger). Compartilham as mesmas
 * regras de mensageria: janela de 24h, sem iniciar conversa e — o que importa
 * aqui — nenhuma operação sobre mensagem JÁ ENVIADA.
 */
export const ehMetaSocial = (plataforma?: string | null): boolean =>
  plataforma === 'INSTAGRAM_LOGIN' ||
  plataforma === 'INSTAGRAM_META' ||
  plataforma === 'MESSENGER_META';

/**
 * Inbox em que mensagem apagada pelo CLIENTE deve sumir da conversa em vez de
 * virar a bolha "mensagem apagada".
 *
 * No WhatsApp o rastro é o comportamento esperado do canal. No Instagram e no
 * Messenger o "unsend" pede o contrário: a diretriz da Meta é que o conteúdo
 * deixe de ser exibido. O servidor já apaga o conteúdo da linha nessas
 * plataformas e filtra a mensagem do histórico — este predicado é o par disso no
 * tempo real e na renderização.
 */
export const plataformaOcultaMensagemApagada = ehMetaSocial;

/**
 * Inbox em que apagar/editar mensagem que NÓS enviamos é possível.
 *
 * A API da Meta não expõe unsend/edição de mensagem enviada em nenhuma das
 * superfícies sociais — só o cliente apaga, pelo app. Deixar o botão habilitado
 * ali só produz um erro do servidor depois do clique.
 */
export const permiteApagarMensagemEnviada = (plataforma?: string | null): boolean =>
  !ehMetaSocial(plataforma);

/** Inbox capaz de enviar templates oficiais Meta (tem metade Cloud API). */
export const suportaTemplatesMeta = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_META' || plataforma === 'WHATSAPP_HYBRID_COEXISTENCE';

/**
 * Inbox cujo **envio em massa** (campanha e ação em lote de mensagem) só pode
 * sair como **template oficial Meta aprovado**.
 *
 * Diferente de {@link ehWhatsappSomenteOficial}, coexistência híbrida entra
 * aqui: a metade Evolution continua atendendo a conversa 1:1 normalmente, mas
 * massa por ela é o que queima o número (banimento por reclamação de spam num
 * dispositivo companheiro derruba as DUAS metades, inclusive a oficial). Em
 * massa, então, o híbrido é tratado como oficial puro: só template aprovado,
 * pela Cloud API.
 *
 * Regras de conversa (janela de 24h, editar/apagar, grupos) NÃO usam este gate
 * — elas seguem o transporte derivado do `idExterno` (ver `utils/transporte`).
 */
export const campanhaSomenteTemplateMeta = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_META' || plataforma === 'WHATSAPP_HYBRID_COEXISTENCE';

/**
 * Plataformas sem AGENDA de contatos para sincronizar.
 *
 * `WHATSAPP_META` puro fala só Cloud API, e a Cloud API não expõe agenda
 * nenhuma; as superfícies sociais da Meta (`INSTAGRAM_*`, `MESSENGER_META`)
 * também não. Nessas quatro o `MetaChatsAdapter.buscarContatos()` devolve `[]`
 * por construção: a sincronização terminava com sucesso e importava ZERO
 * contatos, que é pior que não oferecer a ação.
 *
 * Coexistência híbrida fica de fora da lista de propósito: lá existe a metade
 * Evolution, e o servidor já escolhe o `EvolutionChatsAdapter` para ela
 * (`ChatsAdapterFactory.get`) — a sincronização opera pelo lado do QR code.
 */
export const PLATAFORMAS_SEM_AGENDA_DE_CONTATOS: readonly string[] = [
  'WHATSAPP_META',
  'INSTAGRAM_META',
  'MESSENGER_META',
  'INSTAGRAM_LOGIN',
];

/**
 * Inbox que pode ser ORIGEM de uma sincronização de contatos.
 *
 * Inbox sem plataforma (legado) passa: esconder um inbox que o operador usa
 * hoje seria pior que mostrar um que talvez não sirva — mesma regra de
 * {@link ehPlataformaCompativelComTipoContato}.
 */
export const podeSincronizarContatos = (plataforma?: string | null): boolean =>
  !plataforma || !PLATAFORMAS_SEM_AGENDA_DE_CONTATOS.includes(plataforma);

/**
 * Inbox capaz de enviar um template do WHIZ (texto livre montado por nós).
 *
 * Exatamente o complemento de {@link suportaTemplatesMeta}: onde existe metade
 * Cloud API, o envio de massa/modelo tem de sair como template oficial
 * aprovado, então o template do Whiz não chega a ser ofertado. Coexistência
 * entra junto pela mesma razão de {@link campanhaSomenteTemplateMeta}.
 *
 * Inbox sem plataforma (legado) aceita, pela regra de não esconder o que já
 * funciona hoje.
 */
export const aceitaTemplateWhiz = (plataforma?: string | null): boolean =>
  !suportaTemplatesMeta(plataforma);

/** Motivo, em texto de UI, de um inbox não aceitar template do Whiz. `null` quando aceita. */
export const motivoBloqueioTemplateWhiz = (plataforma?: string | null): string | null =>
  aceitaTemplateWhiz(plataforma)
    ? null
    : `${rotuloPlataforma(plataforma)} não permite templates que não sejam da Meta: a Cloud API só aceita template oficial aprovado.`;

/**
 * Inbox cujo envio LIVRE depende da janela de atendimento de 24h da Meta.
 *
 * Nas quatro, a Meta só aceita mensagem fora de template dentro de 24h contadas
 * a partir da última mensagem DO CONTATO. Passada a janela, o envio é recusado —
 * e, quando sai por um caminho que já respondeu 200 (ver
 * [[falha-meta-pos-envio-131049]]), a recusa só aparece depois.
 *
 * `WHATSAPP_HYBRID_COEXISTENCE` fica de fora de propósito, pela mesma razão de
 * {@link ehWhatsappSomenteOficial}: lá existe a metade Evolution, que envia
 * livremente a qualquer momento.
 */
export const temJanelaDeAtendimento24h = (plataforma?: string | null): boolean =>
  ehWhatsappSomenteOficial(plataforma) || ehMetaSocial(plataforma);

/** Inbox conectável por QR code (transporte não-oficial). */
export const temConexaoQr = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_EVOLUTION' ||
  plataforma === 'WHATSAPP_ZAPI' ||
  plataforma === 'WHATSAPP_HYBRID_COEXISTENCE';

/** Inbox com metade Evolution (servidor Evolution, sincronização de foto, grupos). */
export const temMetadeEvolution = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_EVOLUTION' || plataforma === 'WHATSAPP_HYBRID_COEXISTENCE';

/**
 * Inbox de coexistência — as DUAS metades (oficial Cloud API + QR Evolution) no
 * mesmo número.
 *
 * Existe porque o estado de conexão dele não cabe num booleano: `isConnected`
 * segue a metade OFICIAL (é o que o `GET /status` devolve em `connected`) e
 * `evolutionConectado` segue a metade QR. Quem trata conexão precisa saber de
 * qual metade está falando.
 */
export const ehCoexistencia = (plataforma?: string | null): boolean =>
  plataforma === 'WHATSAPP_HYBRID_COEXISTENCE';

/**
 * Inbox que aceita vínculo de agente bot-IA.
 *
 * Passou a ser TODAS as plataformas. A exclusão das superfícies puramente oficiais existia
 * por uma razão técnica que deixou de valer: o repasse de eventos ao bot-ia só sabia falar
 * o formato da Evolution, então um inbox Cloud API / Instagram / Messenger simplesmente
 * nunca chegava lá. Com o repasse saindo do processamento e levando o formato agnóstico de
 * canal, os quatro passaram a funcionar como qualquer outro.
 *
 * A restrição que RESTA é outra, e mora no vínculo, não aqui: uma Instância do bot-ia só
 * pode ser vinculada a um inbox da MESMA plataforma para a qual ela foi criada — ver
 * `instanciaCombinaComInbox`.
 */
export const suportaBotIa = (_plataforma?: string | null): boolean => true;

/**
 * Plataformas de inbox atendidas por um transporte PRÓPRIO do bot-ia (Evolution direta,
 * Evolution via gateway, whatsmeow, whiz v1) — isto é, por uma Instância que não nasceu
 * de um inbox do whiz v2.
 *
 * São só as de transporte QR/Z-API. Todas essas Instâncias falam com um WhatsApp
 * não-oficial; nenhuma sabe falar Cloud API nem as APIs de página do Instagram e do
 * Messenger.
 */
const PLATAFORMAS_DE_TRANSPORTE_PROPRIO_BOT_IA = ['WHATSAPP_EVOLUTION', 'WHATSAPP_ZAPI'];

/**
 * A Instância do bot-ia serve ESTE inbox?
 *
 * Duas regras, nesta ordem:
 *
 * 1. **Instância do whiz v2** (`type: 'WHIZV2'`) guarda, em `whizV2Info.plataforma`, a
 *    plataforma do inbox que a originou, e só serve inboxes da MESMA plataforma. Vincular
 *    uma Instância criada para WhatsApp a um inbox do Instagram produz um par que conversa
 *    por transportes diferentes — o envio vai por um canal e o histórico é o de outro. Não
 *    há erro visível: só um Agente que responde no lugar errado.
 *
 * 2. **Instância de transporte próprio** (`DIRECT-EVOLUTION`, `GATED-EVOLUTION`,
 *    `WHATSMEOW-STAGING`, `WHIZV1-*`) não nasceu de inbox nenhum: ela aponta para a própria
 *    Evolution/whatsmeow. Serve só onde o inbox também atende por QR — `WHATSAPP_EVOLUTION`
 *    e `WHATSAPP_ZAPI`. Num `WHATSAPP_META` não há metade QR para ela falar, e num
 *    `WHATSAPP_HYBRID_COEXISTENCE` a metade QR é OUTRA instância (a do próprio inbox): o
 *    Agente responderia por um número que não é o do inbox.
 *
 * `plataforma` ausente numa Instância WHIZV2 (vínculo anterior a esta feature, ainda não
 * visitado por um refresh de status) segue tratada como COMPATÍVEL: bloquear por
 * desconhecimento tiraria da tela instâncias que funcionam hoje.
 */
export const instanciaCombinaComInbox = (
  plataformaInstancia?: string | null,
  plataformaInbox?: string | null,
  tipoInstancia?: string | null,
): boolean => {
  if (!plataformaInbox) return true;

  // Instância que não é do whiz v2 → transporte próprio, regra 2.
  if (tipoInstancia && tipoInstancia !== 'WHIZV2') {
    return PLATAFORMAS_DE_TRANSPORTE_PROPRIO_BOT_IA.includes(plataformaInbox);
  }

  if (!plataformaInstancia) return true;
  return plataformaInstancia === plataformaInbox;
};

// ─── Abertura manual de atendimento ─────────────────────────────────────────
// Espelho das regras do servidor (`atendimentos/atendimento-plataforma.util.ts`).
// O backend recusa com 400 de todo jeito; aqui é o que impede o operador de
// chegar até o clique.

/**
 * Plataformas que **não podem iniciar** um atendimento.
 *
 * Nas três a Meta só abre a janela de mensagem depois de uma mensagem DO
 * USUÁRIO — não existe primeiro contato partindo da página/conta. Abrir pelo
 * modal de contatos produzia um atendimento inerte: o operador digitava e o
 * envio falhava depois, sem nenhum aviso antes.
 */
export const PLATAFORMAS_SEM_ATENDIMENTO_MANUAL: readonly string[] = [
  'INSTAGRAM_LOGIN',
  'INSTAGRAM_META',
  'MESSENGER_META',
];

export const podeIniciarAtendimentoManual = (plataforma?: string | null): boolean =>
  !plataforma || !PLATAFORMAS_SEM_ATENDIMENTO_MANUAL.includes(plataforma);

/** Tipo de contato (`Contatos.tipo` no backend). */
export type TipoContato = 'WHATSAPP' | 'INSTAGRAM' | 'FACEBOOK';

/**
 * Plataformas de inbox coerentes com cada tipo de contato. O identificador muda
 * com o canal (telefone, IGSID, PSID), então cruzar canais gera um atendimento
 * que nunca entrega nada.
 *
 * Separado de {@link podeIniciarAtendimentoManual} de propósito: este mapa monta
 * a LISTA de inboxes de origem, aquele decide se a ação é permitida.
 */
export const PLATAFORMAS_POR_TIPO_CONTATO: Readonly<Record<TipoContato, readonly string[]>> = {
  WHATSAPP: ['WHATSAPP_EVOLUTION', 'WHATSAPP_ZAPI', 'WHATSAPP_META', 'WHATSAPP_HYBRID_COEXISTENCE'],
  INSTAGRAM: ['INSTAGRAM_LOGIN', 'INSTAGRAM_META'],
  FACEBOOK: ['MESSENGER_META'],
};

export const plataformasParaTipoContato = (tipo?: string | null): readonly string[] =>
  PLATAFORMAS_POR_TIPO_CONTATO[tipo as TipoContato] ?? [];

/**
 * Inbox sem plataforma (legado) não é filtrado: esconder um inbox que o operador
 * usa hoje seria pior que mostrar um que talvez não sirva.
 */
export const ehPlataformaCompativelComTipoContato = (
  plataforma?: string | null,
  tipo?: string | null,
): boolean => {
  if (!plataforma) return true;
  const aceitas = plataformasParaTipoContato(tipo);
  // Tipo desconhecido → não filtra (mesma razão do inbox sem plataforma).
  return aceitas.length === 0 || aceitas.includes(plataforma);
};

/**
 * Motivo, em texto de UI, de um inbox não poder receber atendimento manual.
 * `null` quando pode.
 */
export const motivoBloqueioAtendimentoManual = (plataforma?: string | null): string | null =>
  podeIniciarAtendimentoManual(plataforma)
    ? null
    : `${rotuloPlataforma(plataforma)} não permite iniciar conversa: a Meta libera o envio só depois de uma mensagem do próprio contato.`;

// ─── Conversão livre de plataforma ──────────────────────────────────────────

/**
 * As três plataformas que descrevem o MESMO número por combinações das mesmas
 * duas metades (oficial Cloud API + QR Evolution) e por isso convertem
 * livremente entre si, preservando o inbox (`POST /inboxes/:id/converter`).
 *
 * `WHATSAPP_ZAPI` fica de fora: outro provedor, sem metade compartilhada.
 */
export const PLATAFORMAS_WHATSAPP_CONVERSIVEIS: readonly string[] = [
  'WHATSAPP_EVOLUTION',
  'WHATSAPP_META',
  'WHATSAPP_HYBRID_COEXISTENCE',
];

export const ehPlataformaConversivel = (plataforma?: string | null): boolean =>
  !!plataforma && PLATAFORMAS_WHATSAPP_CONVERSIVEIS.includes(plataforma);

/** Metades que cada plataforma exige — o que descreve toda conversão. */
export const metadesDaPlataforma = (plataforma?: string | null): { oficial: boolean; qr: boolean } => ({
  oficial: plataforma === 'WHATSAPP_META' || plataforma === 'WHATSAPP_HYBRID_COEXISTENCE',
  qr: plataforma === 'WHATSAPP_EVOLUTION' || plataforma === 'WHATSAPP_HYBRID_COEXISTENCE',
});

/** Opções do seletor de plataforma na criação de inbox (admin). */
export const OPCOES_PLATAFORMA: { label: string; value: InboxType }[] = [
  'INSTAGRAM_META',
  'INSTAGRAM_LOGIN',
  'MESSENGER_META',
  'WHATSAPP_EVOLUTION',
  'WHATSAPP_ZAPI',
  'WHATSAPP_META',
  'WHATSAPP_HYBRID_COEXISTENCE',
].map((value) => ({ label: rotuloPlataforma(value), value: value as InboxType }));
